import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../core/services/auth.service';
import { DirectusService } from '../core/services/directus.service';

@Component({
  standalone: true,
  imports: [CommonModule],
  template: `
    <main class="wrap">
      <section class="card">
        <h1>Signing you in...</h1>
        <p *ngIf="!error">Finalizing Auth0 session with Directus.</p>
        <p class="error" *ngIf="error">{{ error }}</p>
      </section>
    </main>
  `,
  styles: [
    `
      .wrap {
        min-height: 100vh;
        display: grid;
        place-items: center;
        background: #0f172a;
        padding: 1rem;
      }
      .card {
        width: min(500px, 100%);
        background: #fff;
        border-radius: 12px;
        border: 1px solid #dbeafe;
        padding: 1rem;
      }
      h1,
      p {
        margin: 0;
      }
      p {
        margin-top: 0.4rem;
      }
      .error {
        color: #991b1b;
      }
    `
  ]
})
export class AuthCallbackComponent implements OnInit {
  error = '';

  constructor(
    private readonly auth: AuthService,
    private readonly directus: DirectusService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      const error = this.route.snapshot.queryParamMap.get('error');
      const errorDescription = this.route.snapshot.queryParamMap.get('error_description');
      if (error) {
        throw new Error(errorDescription || error);
      }

      let accessToken = this.route.snapshot.queryParamMap.get('access_token') || '';
      if (!accessToken && window.location.hash) {
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        accessToken = hashParams.get('access_token') || '';
      }

      if (!accessToken) {
        const code = this.route.snapshot.queryParamMap.get('code') || '';
        if (code) {
          const provider = this.directus.ssoProviderFromConfig() || 'auth0';
          const callbackParams: Record<string, string> = {};
          for (const key of this.route.snapshot.queryParamMap.keys) {
            const value = this.route.snapshot.queryParamMap.get(key);
            if (value) callbackParams[key] = value;
          }
          accessToken = await this.directus.exchangeSsoCode(provider, callbackParams);
        }
      }

      await this.auth.completeSsoLogin(accessToken || undefined);
      const rawNext = this.auth.consumeNextPath();
      const nextPath = this.auth.getSafeNextPath(rawNext);
      await this.router.navigateByUrl(nextPath);
    } catch (error: unknown) {
      this.error =
        error instanceof Error
          ? `${error.message}. Check Directus CORS and callback URL settings.`
          : 'SSO callback failed';
    }
  }
}
